import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../ui/dialog";
import { Button } from "../../../ui/button";
import { Label } from "../../../ui/label";
import { Textarea } from "../../../ui/textarea";
import { Input } from "../../../ui/input";
import { Badge } from "../../../ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../ui/popover";
import {
  Sparkles,
  Loader2,
  Plus,
  Check,
  Lightbulb,
  Wand2,
} from "lucide-react";
import { UserTakeaway } from "../../../../api/userTakeaways";
import { UserCategory, createCategory } from "../../../../api/userCategories";
import { suggestCategories, SuggestedCategory } from "../../../../api/insightAI";
import { cn } from "../../../../lib/utils";
import useRetryFetch from "../../../../hooks/useRetryFetch";
import { toast } from "sonner";
import SpaceTagPicker from "../../../shared/SpaceTagPicker";
import { useSpaces } from "../../../../context/SpaceProvider";

interface InsightDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (data: {
    content: string;
    category?: string;
    spaceIds?: string[];
  }) => Promise<void>;
  categories: UserCategory[];
  item?: UserTakeaway;
  onCategoryCreated?: (category: UserCategory) => void;
  /** Pre-select a space when creating from within a space view */
  defaultSpaceId?: string;
}

// Predefined colors for new categories
const CATEGORY_COLORS = [
  "#3B82F6", // Blue
  "#10B981", // Emerald
  "#F59E0B", // Amber
  "#EF4444", // Red
  "#8B5CF6", // Purple
  "#EC4899", // Pink
  "#06B6D4", // Cyan
  "#84CC16", // Lime
  "#F97316", // Orange
  "#6366F1", // Indigo
];

const InsightDialog: React.FC<InsightDialogProps> = ({
  open,
  onOpenChange,
  onSave,
  categories,
  item,
  onCategoryCreated,
  defaultSpaceId,
}) => {
  const { t } = useTranslation();
  const retryFetch = useRetryFetch();
  const { hasSpaces } = useSpaces();
  const [content, setContent] = useState("");
  const [categoryId, setCategoryId] = useState<string | undefined>(undefined);
  const [spaceIds, setSpaceIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  // AI suggestion state
  const [isSuggesting, setIsSuggesting] = useState(false);
  const [suggestions, setSuggestions] = useState<SuggestedCategory[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isCreatingCategory, setIsCreatingCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [newCategoryColor, setNewCategoryColor] = useState(CATEGORY_COLORS[0]);

  // Load existing data when editing
  useEffect(() => {
    if (item) {
      setContent(item.content || "");
      setCategoryId(item.category?._id);
      setSpaceIds(item.spaceIds || []);
    } else {
      setContent("");
      setCategoryId(undefined);
      setSpaceIds(defaultSpaceId ? [defaultSpaceId] : []);
    }
    // Reset suggestions when dialog opens/closes
    setSuggestions([]);
    setShowSuggestions(false);
    setIsCreatingCategory(false);
  }, [item, open, defaultSpaceId]);

  const handleSuggestCategories = async () => {
    if (!content.trim() || content.length < 10) {
      toast.error(t("content_too_short_for_suggestion") || "Please enter more content for better suggestions");
      return;
    }

    setIsSuggesting(true);
    setSuggestions([]);

    try {
      const response = await suggestCategories(retryFetch, {
        content: content.trim(),
        existingCategoryIds: categories.map((c) => c._id),
      });

      if (response.status === "SUCCESS" && response.suggestions?.length > 0) {
        setSuggestions(response.suggestions);
        setShowSuggestions(true);
      } else {
        // Fallback to local suggestion
        const localSuggestions = suggestCategoriesLocally(content);
        setSuggestions(localSuggestions);
        setShowSuggestions(true);
      }
    } catch (error) {
      console.error("Failed to get AI suggestions, using local fallback:", error);
      const localSuggestions = suggestCategoriesLocally(content);
      setSuggestions(localSuggestions);
      setShowSuggestions(true);
    } finally {
      setIsSuggesting(false);
    }
  };

  // Local fallback for category suggestions
  const suggestCategoriesLocally = (text: string): SuggestedCategory[] => {
    const contentLower = text.toLowerCase();
    const results: SuggestedCategory[] = [];

    // First, try to match existing categories
    for (const category of categories) {
      const categoryLower = category.name.toLowerCase();
      const keywords = categoryLower.split(/\s+/);

      let matchScore = 0;
      for (const keyword of keywords) {
        if (keyword.length > 2 && contentLower.includes(keyword)) {
          matchScore += 1;
        }
      }

      if (matchScore > 0 || contentLower.includes(categoryLower)) {
        results.push({
          categoryId: category._id,
          categoryName: category.name,
          confidence: Math.min(0.9, 0.5 + matchScore * 0.2),
          isNew: false,
          reason: t("matches_existing_category") || "Matches your existing category",
        });
      }
    }

    // Suggest new categories based on common themes
    const themeKeywords: Record<string, { keywords: string[]; color: string }> = {
      "Leadership": { keywords: ["lead", "leader", "manage", "team", "delegate", "vision"], color: "#3B82F6" },
      "Communication": { keywords: ["communicate", "listen", "speak", "feedback", "conversation", "discuss"], color: "#10B981" },
      "Personal Growth": { keywords: ["grow", "learn", "develop", "improve", "progress", "skill"], color: "#8B5CF6" },
      "Strategy": { keywords: ["strategy", "plan", "goal", "objective", "vision", "mission"], color: "#F59E0B" },
      "Productivity": { keywords: ["productive", "efficient", "time", "focus", "priorit", "task"], color: "#06B6D4" },
      "Relationships": { keywords: ["relationship", "trust", "connect", "collaborate", "partner"], color: "#EC4899" },
      "Creativity": { keywords: ["creative", "innovat", "idea", "design", "think", "brainstorm"], color: "#F97316" },
      "Mindset": { keywords: ["mindset", "attitude", "belief", "perspective", "mental", "think"], color: "#6366F1" },
    };

    for (const [themeName, { keywords, color }] of Object.entries(themeKeywords)) {
      // Skip if we already have this category
      if (categories.some((c) => c.name.toLowerCase() === themeName.toLowerCase())) {
        continue;
      }

      const matchCount = keywords.filter((kw) => contentLower.includes(kw)).length;
      if (matchCount >= 2) {
        results.push({
          categoryName: themeName,
          confidence: Math.min(0.85, 0.4 + matchCount * 0.15),
          isNew: true,
          suggestedColor: color,
          reason: t("suggested_based_on_content") || "Suggested based on your content",
        });
      }
    }

    // Sort by confidence and return top 4
    return results.sort((a, b) => b.confidence - a.confidence).slice(0, 4);
  };

  const handleSelectSuggestion = (suggestion: SuggestedCategory) => {
    if (suggestion.isNew) {
      // Set up for creating new category
      setNewCategoryName(suggestion.categoryName);
      setNewCategoryColor(suggestion.suggestedColor || CATEGORY_COLORS[0]);
      setIsCreatingCategory(true);
    } else if (suggestion.categoryId) {
      setCategoryId(suggestion.categoryId);
      setShowSuggestions(false);
    }
  };

  const handleCreateNewCategory = async () => {
    if (!newCategoryName.trim()) return;

    try {
      const response = await createCategory(retryFetch, {
        name: newCategoryName.trim(),
        color: newCategoryColor,
        type: "insight",
      });

      if (response.status === "CREATED" && response.category) {
        setCategoryId(response.category._id);
        onCategoryCreated?.(response.category);
        toast.success(t("category_created") || "Category created successfully");
        setIsCreatingCategory(false);
        setShowSuggestions(false);
      }
    } catch (error) {
      console.error("Failed to create category:", error);
      toast.error(t("failed_to_create_category") || "Failed to create category");
    }
  };

  const handleSave = async () => {
    if (!content.trim()) return;

    setSaving(true);
    try {
      await onSave({
        content: content.trim(),
        category: categoryId || undefined,
        spaceIds: spaceIds.length > 0 ? spaceIds : undefined,
      });
      onOpenChange(false);
    } catch (error) {
      console.error("Failed to save insight:", error);
    } finally {
      setSaving(false);
    }
  };

  const selectedCategory = categories.find((c) => c._id === categoryId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lightbulb className="h-5 w-5 text-amber-500" />
            {item ? t("edit_insight") || t("edit") : t("add_insight")}
          </DialogTitle>
          <DialogDescription>
            {item
              ? t("edit_insight_description") || "Update your insight below"
              : t("add_insight_description") || "Capture a thought, learning, or realization"}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {/* Content */}
          <div className="grid gap-2">
            <Label htmlFor="content">{t("content")}</Label>
            <Textarea
              id="content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder={t("enter_insight_content") || "What did you learn or realize?"}
              rows={4}
              className="resize-none"
            />
          </div>

          {/* Category with AI Suggestion */}
          <div className="grid gap-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="category">{t("category")}</Label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleSuggestCategories}
                disabled={isSuggesting || content.length < 10}
                className="h-7 text-xs text-violet-600 hover:text-violet-700 hover:bg-violet-50"
              >
                {isSuggesting ? (
                  <>
                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                    {t("suggesting") || "Suggesting..."}
                  </>
                ) : (
                  <>
                    <Sparkles className="h-3 w-3 mr-1" />
                    {t("suggest_category") || "Suggest Category"}
                  </>
                )}
              </Button>
            </div>

            <Select
              value={categoryId || "none"}
              onValueChange={(value) => setCategoryId(value === "none" ? undefined : value)}
            >
              <SelectTrigger id="category">
                <SelectValue placeholder={t("select_category")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{t("no_category") || "No Category"}</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat._id} value={cat._id}>
                    <div className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full border"
                        style={{ backgroundColor: cat.color }}
                      />
                      {cat.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* AI Suggestions */}
            {showSuggestions && suggestions.length > 0 && (
              <div className="mt-2 p-3 rounded-lg bg-violet-50 border border-violet-100">
                <div className="flex items-center gap-2 mb-2">
                  <Wand2 className="h-4 w-4 text-violet-500" />
                  <span className="text-xs font-medium text-violet-700">
                    {t("ai_suggested_categories") || "AI Suggested Categories"}
                  </span>
                </div>

                {!isCreatingCategory ? (
                  <div className="flex flex-wrap gap-2">
                    {suggestions.map((suggestion, index) => (
                      <button
                        key={index}
                        onClick={() => handleSelectSuggestion(suggestion)}
                        className={cn(
                          "inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-all",
                          "hover:ring-2 hover:ring-violet-300",
                          suggestion.isNew
                            ? "bg-white border border-dashed border-violet-300 text-violet-700"
                            : "bg-white border border-violet-200 text-gray-700"
                        )}
                      >
                        {!suggestion.isNew && (
                          <div
                            className="w-2.5 h-2.5 rounded-full"
                            style={{
                              backgroundColor:
                                categories.find((c) => c._id === suggestion.categoryId)?.color ||
                                suggestion.suggestedColor,
                            }}
                          />
                        )}
                        {suggestion.isNew && <Plus className="h-3 w-3 text-violet-500" />}
                        {suggestion.categoryName}
                        <Badge
                          variant="secondary"
                          className="ml-1 h-4 text-[10px] bg-violet-100 text-violet-600"
                        >
                          {Math.round(suggestion.confidence * 100)}%
                        </Badge>
                      </button>
                    ))}
                  </div>
                ) : (
                  // New category creation form
                  <div className="space-y-3">
                    <p className="text-xs text-violet-600">
                      {t("create_new_category") || "Create new category"}
                    </p>
                    <div className="flex items-center gap-2">
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            className="w-8 h-8 rounded-md border-2 border-gray-200 hover:border-gray-300 transition-colors"
                            style={{ backgroundColor: newCategoryColor }}
                          />
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-2">
                          <div className="grid grid-cols-5 gap-1">
                            {CATEGORY_COLORS.map((color) => (
                              <button
                                key={color}
                                onClick={() => setNewCategoryColor(color)}
                                className={cn(
                                  "w-6 h-6 rounded-md transition-transform hover:scale-110",
                                  newCategoryColor === color && "ring-2 ring-offset-1 ring-gray-400"
                                )}
                                style={{ backgroundColor: color }}
                              />
                            ))}
                          </div>
                        </PopoverContent>
                      </Popover>
                      <Input
                        value={newCategoryName}
                        onChange={(e) => setNewCategoryName(e.target.value)}
                        placeholder={t("category_name") || "Category name"}
                        className="flex-1 h-8 text-sm"
                      />
                      <Button
                        size="sm"
                        onClick={handleCreateNewCategory}
                        disabled={!newCategoryName.trim()}
                        className="h-8 bg-violet-500 hover:bg-violet-600"
                      >
                        <Check className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setIsCreatingCategory(false)}
                        className="h-8"
                      >
                        {t("cancel")}
                      </Button>
                    </div>
                  </div>
                )}

                {!isCreatingCategory && (
                  <p className="mt-2 text-[10px] text-violet-500">
                    {t("click_to_select_or_create") || "Click to select, or click + to create new"}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Identity Space assignment — only shown when user has spaces */}
          {hasSpaces && (
            <div className="grid gap-2">
              <Label>{t("identity_space") || "Identity Space"}</Label>
              <SpaceTagPicker
                selectedSpaceIds={spaceIds}
                onChange={setSpaceIds}
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            {t("cancel")}
          </Button>
          <Button
            onClick={handleSave}
            disabled={!content.trim() || saving}
            className="bg-amber-500 hover:bg-amber-600"
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {t("saving") || "Saving..."}
              </>
            ) : (
              t("save")
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InsightDialog;
